import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { formatIDR, formatDateTime, formatDuration } from '@/lib/format';
import { Input } from '@/components/ui/input';
import { Search, Gamepad2, UtensilsCrossed } from 'lucide-react';

const filters = [
  { key: 'all', label: 'Semua', testid: 'filter-transaction-all' },
  { key: 'rental', label: 'Rental PS', testid: 'filter-transaction-rental' },
  { key: 'food', label: 'Makanan & Minuman', testid: 'filter-transaction-food' },
];

export default function History() {
  const [txs, setTxs] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      const data = await api.allTransactions();
      setTxs(data);
    };
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  const filtered = useMemo(() => {
    return txs.filter(t => {
      if (filter !== 'all' && t.type !== filter) return false;
      if (search && !t.label.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [txs, filter, search]);

  const total = filtered.reduce((sum, t) => sum + (t.total || 0), 0);

  return (
    <div className="space-y-6" data-testid="history-page">
      <div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Riwayat Transaksi</h2>
        <p className="text-slate-400 text-sm mt-1">Log lengkap semua transaksi rental dan penjualan.</p>
      </div>

      <div className="rounded-xl bg-[#131B2E] border border-slate-800 p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <div className="flex gap-2 flex-wrap">
            {filters.map(f => (
              <button
                key={f.key}
                data-testid={f.testid}
                onClick={() => setFilter(f.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  filter === f.key
                    ? 'bg-cyan-500/15 text-cyan-300 border border-cyan-400/50'
                    : 'text-slate-400 border border-slate-700 hover:border-slate-500 hover:text-slate-100'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="sm:ml-auto relative flex-1 sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              data-testid="input-search-transactions"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari nama unit/item..."
              className="pl-9 bg-[#0E1424] border-slate-700 text-slate-100"
            />
          </div>
        </div>

        <div className="flex items-center justify-between mb-3 pb-3 border-b border-slate-800">
          <p className="text-sm text-slate-400">
            <span data-testid="text-transaction-count">{filtered.length}</span> transaksi
          </p>
          <p className="text-sm">
            Subtotal: <span className="text-cyan-400 font-mono-tactical font-bold" data-testid="text-transaction-subtotal">{formatIDR(total)}</span>
          </p>
        </div>

        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left text-xs font-mono-tactical uppercase tracking-widest text-slate-500 border-b border-slate-800">
                <th className="px-3 py-3">Waktu</th>
                <th className="px-3 py-3">Jenis</th>
                <th className="px-3 py-3">Item / Unit</th>
                <th className="px-3 py-3 text-right">Qty / Durasi</th>
                <th className="px-3 py-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-slate-500">Belum ada transaksi</td>
                </tr>
              )}
              {filtered.map(t => (
                <tr key={`${t.type}-${t.id}`} data-testid={`tx-row-${t.id}`} className="border-b border-slate-800/60 hover:bg-slate-800/20">
                  <td className="px-3 py-3 text-slate-300 whitespace-nowrap">{formatDateTime(t.at)}</td>
                  <td className="px-3 py-3">
                    {t.type === 'rental' ? (
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded border border-cyan-500/40 bg-cyan-500/10 text-cyan-400 text-xs font-mono-tactical uppercase tracking-wider">
                        <Gamepad2 className="w-3 h-3" /> Rental
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded border border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-400 text-xs font-mono-tactical uppercase tracking-wider">
                        <UtensilsCrossed className="w-3 h-3" /> Makanan
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div className="text-slate-100 font-medium">{t.label}</div>
                    {t.customer && <div className="text-xs text-slate-500">Pelanggan: {t.customer}</div>}
                  </td>
                  <td className="px-3 py-3 text-right text-slate-300 font-mono-tactical">
                    {t.type === 'rental' ? formatDuration(t.qty) : `${t.qty} pcs`}
                  </td>
                  <td className="px-3 py-3 text-right font-mono-tactical font-semibold text-emerald-400">
                    {formatIDR(t.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
